/**
 * FRONT-1638: copy for the reconnect give-up banner (FRONT-773), keyed off
 * the closed `streamFailureReason` the /history contract derives server-side
 * (see `deriveStreamFailureReason` in src/agents/stream-lifecycle-meta.ts).
 * `undefined` — no reason landed on the row — returns today's exact fixed
 * copy, so a row with no derivable reason is a no-op regression-wise.
 *
 * The reason exists on the row only if a 5s progress tick landed after the
 * recovery dispatch (see that file's docblock); that's the honest fallback
 * this module leans on rather than trying to close.
 */

/** Local mirror of the backend's closed union — the frontend does not
 * import from src/, so this must be kept in sync by hand if the backend set
 * changes (see stream-lifecycle-meta.ts's STREAM_FAILURE_REASONS).
 * `STREAM_FAILURE_REASONS` is exported so a metadata reader (message-bubble.tsx's
 * readMessageMeta) can allowlist against this one list instead of hand-typing
 * a second copy of the same three strings. */
export const STREAM_FAILURE_REASONS = ['upstream-payload-loss', 'output-token-limit', 'malformed-tool-input'] as const;

export type StreamFailureReason = (typeof STREAM_FAILURE_REASONS)[number];

export interface ReconnectBannerCopy {
  headline: string;
  body: string;
}

// The headline never varies by reason — one constant, referenced everywhere,
// so a future revision can't update three of four occurrences and miss one.
const HEADLINE = 'EDDY couldn’t reconnect to this reply';

const DEFAULT_COPY: ReconnectBannerCopy = {
  headline: HEADLINE,
  body:
    'The reply was cut off and reconnecting was given up after several tries — the answer above may be incomplete; ask again to continue.',
};

const COPY_BY_REASON: Record<StreamFailureReason, ReconnectBannerCopy> = {
  'upstream-payload-loss': {
    headline: HEADLINE,
    body:
      'An upstream payload was lost mid-reply and EDDY was re-sending that one call when the connection was lost — the answer above may be incomplete; if that part never appeared, ask EDDY to re-send just it.',
  },
  'output-token-limit': {
    headline: HEADLINE,
    body:
      'The reply hit its output limit while writing a tool call, and the connection was lost before it recovered — asking again verbatim will hit the same limit again; ask EDDY to split the work into smaller steps.',
  },
  'malformed-tool-input': {
    headline: HEADLINE,
    body:
      'EDDY produced a malformed tool call and was retrying it when the connection was lost — the answer above may be incomplete; ask again to continue.',
  },
};

export function reconnectBannerCopy(reason: StreamFailureReason | undefined): ReconnectBannerCopy {
  if (reason && Object.prototype.hasOwnProperty.call(COPY_BY_REASON, reason)) {
    return COPY_BY_REASON[reason];
  }
  return DEFAULT_COPY;
}
